import * as geojson from "geojson";
import { DataChangeFeature, DataReviewJob, GroupStatsWire, TermCatalogue } from "../../types/dataReview";
import { bboxOfGeometry } from "../dataReview/geometry";

/** Pure helpers behind the vegetation editor: areas, ordering, grouping
 *  findings by what was looked for, histogram bins and thumbnail crops. */

const R = 6378137;
const rad = (d: number) => (d * Math.PI) / 180;

/** Geodesic area of one ring in m² (the same formula turf uses). */
export function ringAreaM2(ring: geojson.Position[]): number {
  if (ring.length < 3) return 0;
  let total = 0;
  for (let i = 0; i < ring.length; i++) {
    const p1 = ring[i], p2 = ring[(i + 1) % ring.length], p3 = ring[(i + 2) % ring.length];
    total += (rad(p3[0]) - rad(p1[0])) * Math.sin(rad(p2[1]));
  }
  return Math.abs((total * R * R) / 2);
}

export function geometryAreaM2(g: geojson.Geometry | null | undefined): number {
  if (!g) return 0;
  if (g.type === "Polygon") return g.coordinates.reduce((a, ring, i) => a + (i ? -1 : 1) * ringAreaM2(ring), 0);
  if (g.type === "MultiPolygon") return g.coordinates.reduce((a, p) => a + p.reduce((b, ring, i) => b + (i ? -1 : 1) * ringAreaM2(ring), 0), 0);
  return 0;
}

/** "640 m²" or "1.24 ha". */
export const fmtArea = (m2: number): string => {
  const v = Math.abs(m2);
  if (v < 1000) return `${Math.round(v).toLocaleString()} m²`;
  return `${(v / 10_000).toFixed(2)} ha`;
};

export const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";

/** The project boundary as a feature, whatever shape the API gave it. */
export function boundaryFeature(boundary: geojson.Feature | geojson.Geometry | null | undefined): geojson.Feature | null {
  if (!boundary) return null;
  if (boundary.type === "Feature") return boundary;
  if (boundary.type === "Polygon" || boundary.type === "MultiPolygon") return { type: "Feature", properties: {}, geometry: boundary };
  return null;
}

/** Which look-for target a finding belongs to. Paint and older runs have none. */
export const PAINT_TERM = "painted";
export const termOf = (f: DataChangeFeature): string => {
  const a = f.properties.attrs ?? {};
  if (a.painted) return PAINT_TERM;
  if (a.term) return String(a.term);
  if (a.pass !== undefined) return `pass:${a.pass}`;
  return "other";
};

/** Metres per layout pixel at this latitude and zoom (512 px tiles). */
export const metresPerPixel = (lat: number, zoom: number) => (78271.517 * Math.cos(rad(lat))) / 2 ** zoom;

/** One target of a run, as the editor shows it: the server's counts with a
 *  label for the key. */
export interface GroupStats {
  key: string;
  label: string;
  total: number;
  /** Above the cut-off. */
  shown: number;
  hidden: number;
  pending: number;
  accepted: number;
  rejected: number;
  /** The pass this target ran as (the latest, when it ran more than once). */
  pass: number | undefined;
  target: string;
  assetType: string;
  /** Twenty bins of confidence 0..100, over every finding of the target. */
  histogram: number[];
  acceptedM2: number;
}

export function toGroups(wire: GroupStatsWire[] | undefined, catalogue: TermCatalogue | undefined): Record<string, GroupStats> {
  const labels: Record<string, string> = {};
  for (const p of catalogue?.presets ?? []) labels[p.key] = p.label;
  const out: Record<string, GroupStats> = {};
  for (const g of wire ?? []) {
    out[g.key] = {
      key: g.key, label: labelOfTerm(g.key, labels), total: g.total, shown: g.shown, hidden: g.hidden,
      pending: g.pending, accepted: g.accepted, rejected: g.rejected, pass: g.pass ?? undefined,
      target: g.target, assetType: g.asset_type, histogram: g.histogram, acceptedM2: g.accepted_m2,
    };
  }
  return out;
}

export function labelOfTerm(key: string, labels: Record<string, string> = {}): string {
  if (key === PAINT_TERM) return "Painted";
  if (labels[key]) return labels[key];
  if (key.startsWith("custom:")) { const t = key.slice(7); return t.charAt(0).toUpperCase() + t.slice(1); }
  if (key.startsWith("pass:")) return `Pass ${key.slice(5)}`;
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/** The terms a run has asked for, in the order they were asked, from its stats. */
export const termsOfJob = (job: DataReviewJob | null | undefined): string[] => ((job?.stats?.terms as string[] | undefined) ?? []);

export type Overlay = { url: string; coordinates: number[][] };

/** How to crop the run's photo to one shape for a filmstrip thumbnail: the
 *  chunk under the shape, placed and stretched inside a box so the shape's
 *  bounds (padded) fill it, and a viewBox for an outline drawn over it. */
export function thumbCrop(
  overlays: Overlay[], f: DataChangeFeature, boxW: number, boxH: number,
): { url: string; left: number; top: number; width: number; height: number; viewBox: string } | null {
  const b = bboxOfGeometry(f.geometry);
  if (!b || !overlays.length) return null;
  const [cx, cy] = [(b[0][0] + b[1][0]) / 2, (b[0][1] + b[1][1]) / 2];
  const chunk = overlays.find((o) => {
    const xs = o.coordinates.map((c) => c[0]); const ys = o.coordinates.map((c) => c[1]);
    return cx >= Math.min(...xs) && cx <= Math.max(...xs) && cy >= Math.min(...ys) && cy <= Math.max(...ys);
  }) ?? overlays[0];
  const xs = chunk.coordinates.map((c) => c[0]); const ys = chunk.coordinates.map((c) => c[1]);
  const cw = Math.max(...xs) - Math.min(...xs), ch = Math.max(...ys) - Math.min(...ys);
  if (!cw || !ch) return null;
  // The window: the shape's bounds padded by a third, at least as wide as
  // the box's aspect so small shapes are not blown up to blur.
  const padX = (b[1][0] - b[0][0]) * 0.35 + cw * 0.002, padY = (b[1][1] - b[0][1]) * 0.35 + ch * 0.002;
  let w = b[1][0] - b[0][0] + 2 * padX, h = b[1][1] - b[0][1] + 2 * padY;
  const aspect = (boxW / boxH) / (Math.cos(rad(cy)) || 1);
  if (w / h < aspect) w = h * aspect; else h = w / aspect;
  const west = cx - w / 2, north = cy + h / 2;
  const sx = boxW / w, sy = boxH / h;
  return {
    url: chunk.url,
    left: (Math.min(...xs) - west) * sx,
    top: (north - Math.max(...ys)) * sy,
    width: cw * sx,
    height: ch * sy,
    viewBox: `${west} ${-north} ${w} ${h}`,
  };
}

/** An SVG path of a geometry in lng / −lat space, for the thumbnail outline. */
export function geometryPath(g: geojson.Geometry): string {
  const ring = (r: geojson.Position[]) => "M" + r.map((p) => `${p[0]} ${-p[1]}`).join(" L") + "Z";
  if (g.type === "Polygon") return g.coordinates.map(ring).join("");
  if (g.type === "MultiPolygon") return g.coordinates.map((p) => p.map(ring).join("")).join("");
  return "";
}

/** Vegetation picker labels for the two non-class targets. */
export const targetLabel = (t: string) => t === "Cleared" ? "Cleared (no vegetation)" : t === "Unchanged" ? "Leave vegetation as it is" : t;

/** On a clean slate the old vegetation inside the area counts as cleared.
 *  The dark wash that says so only goes on where it hides something: the
 *  vegetation layer showing, with no photo over the area. Over the photo
 *  it would only darken what you are trying to read. */
export const clearedWashVisible = (o: { clean: boolean; vegLayerOn: boolean; photoOver: boolean }) =>
  o.clean && o.vegLayerOn && !o.photoOver;
